/**
 * City × specialty listing SEO experiment (ghane DR, owner approval 2026-09-18).
 *
 * Google shows our listing 4th–5th for "<specialty> <big city>" while nobat.ir / doctoreto lead with
 * «بهترین دکتر … <شهر>». This page already has what they don't: live first free slots and review counts.
 * For routes on the GrowthBook allow-list only, the title/description are rebuilt from the live result,
 * and valid BreadcrumbList + ItemList JSON-LD are emitted (the CMS `jsonld` is a bare {href,name} array
 * with no @context/@type, so Google cannot read it). Everything else keeps the CMS template untouched,
 * so the experiment is a page split: treated routes vs matched control routes, compared in Search Console.
 */

type SeoCrumb = { href?: string; name?: string };
type SearchAction = { title?: string; top_title?: string };
type SearchCard = {
  type?: string;
  title?: string;
  prefix?: string;
  url?: string;
  rates_count?: number;
  /** Average rating out of 5, the same number the card renders next to «نظر». */
  star?: number;
  image?: string;
  actions?: SearchAction[];
};

export type ListingSeoExperiment = {
  title: string;
  description: string;
  jsonlds: object[];
};

const ORIGIN = 'https://www.paziresh24.com';

/**
 * The card's own rating, as an AggregateRating — or undefined when nobody has rated the doctor.
 * Google only accepts a rating the page shows, so this is the number on the card: `star` rounded
 * to one decimal equals the card's Math.round(satisfaction / 20 * 10) / 10 (60 of 60 live cards,
 * 2026-10-12). The profile page computes its own value from the rating dimensions and can differ
 * by 0.1 (4.4 there, 4.5 on the card) — that gap is the profile's, not this list's.
 * `rates_count` counts ratings, not written reviews, hence ratingCount. Anything that is not a
 * finite number in range is dropped rather than published as an invalid rating.
 */
const cardRating = (card: SearchCard) => {
  const count = card.rates_count;
  const value = card.star;
  if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) return undefined;
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0 || value > 5) return undefined;
  return {
    '@type': 'AggregateRating',
    'ratingValue': Math.round(value * 10) / 10,
    'ratingCount': count,
    'bestRating': 5,
    'worstRating': 0,
  };
};

/** Drop keys the API did not send, so the JSON-LD never carries empty or invented values. */
const compact = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== '')) as T;
const faNumber = (n: number) => n.toLocaleString('fa-IR');
const stripHtml = (s: string) => s.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

/** Path of a listing URL without query/hash, always with a trailing slash. */
export const listingPath = (asPath: string) => {
  const path = asPath.split(/[?#]/)[0];
  return path.endsWith('/') ? path : `${path}/`;
};

/** First free in-person slot text on the page («امروز …» beats «فردا …» beats any date). */
export const earliestSlot = (result: SearchCard[] = []): string | null => {
  const slots = result
    .flatMap(card => card.actions ?? [])
    .filter(action => action.title?.startsWith('نوبت') && action.top_title)
    .map(action => stripHtml(action.top_title as string))
    .filter(text => text.startsWith('اولین نوبت'))
    .map(text => text.replace(/^اولین نوبت\s*:\s*/, ''));
  return slots.find(s => s.startsWith('امروز')) ?? slots.find(s => s.startsWith('فردا')) ?? slots[0] ?? null;
};

/**
 * The CMS ships the listing breadcrumb as a bare [{href,name}] array under the JSON-LD media type,
 * so Google parses it, recognises nothing, and discards it without even logging an error: on
 * 2026-10-01 the Breadcrumbs report counted 236K valid items against 759K indexed pages and `/s/`
 * drew no rich result at all (ghane DR-51). Same data, wrapped so it can be read.
 *
 * All or nothing, deliberately. Earlier versions of this function dropped the crumbs they could not
 * use and renumbered the rest, which publishes a hierarchy that does not exist — «پذیرش۲۴ › پوست و
 * مو» for a page that really sits under «اصفهان». A breadcrumb Google cannot read costs us a rich
 * result; a breadcrumb that lies costs us the trust the rich result is for. So one unusable crumb
 * means no breadcrumb, and nothing is invented to fill a gap.
 *
 * Usable means: a non-empty string `name`, and an absolute http(s) `href` — except on the LAST
 * crumb, where Google explicitly does not require `item` ("If the breadcrumb is the last item in
 * the breadcrumb trail, item is not required"). The href is validated, never rewritten: resolving
 * it against our origin would launder `javascript:`, `data:` and `//evil.com/x` into something that
 * looks like ours.
 */
const crumbName = (name: unknown): string | null =>
  typeof name === 'string' && name.trim() ? name.trim() : null;

const BIDI_MARKS = /[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;
const EDGE_BIDI_MARKS = /^[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]+|[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]+$/g;

const absoluteHttpHref = (href: unknown): string | null => {
  if (typeof href !== 'string') return null;
  // trim() leaves the bidi marks a Persian CMS can put on either end, so strip those too — but only
  // at the edges. Removing one from the middle would change the path rather than clean it, so a
  // mark or any whitespace inside makes the href unusable, like any other value we cannot trust.
  // trim first: a mark sitting BEFORE trailing whitespace is not at the edge yet, so stripping
  // before trimming would leave it in place and then reject the whole href.
  const cleaned = href.trim().replace(EDGE_BIDI_MARKS, '').trim();
  if (/\s/.test(cleaned) || BIDI_MARKS.test(cleaned)) return null;
  return /^https?:\/\//i.test(cleaned) ? cleaned : null;
};

export const buildListingBreadcrumbJsonLd = (crumbs: SeoCrumb[] | undefined): object | null => {
  if (!Array.isArray(crumbs) || crumbs.length < 2) return null;
  const itemListElement = [];
  for (let index = 0; index < crumbs.length; index += 1) {
    const name = crumbName(crumbs[index]?.name);
    if (!name) return null;
    const href = absoluteHttpHref(crumbs[index]?.href);
    if (!href && index !== crumbs.length - 1) return null;
    itemListElement.push({ '@type': 'ListItem', position: index + 1, name, ...(href ? { item: href } : {}) });
  }
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement };
};

/**
 * What the listing page hands to <Seo jsonlds>.
 *
 * ADD, never replace. Whatever `seoInfo.jsonld` is, it goes to <Seo> as a single element, which is
 * what the page emits today: one <script> whose body is that value. A BreadcrumbList is prepended
 * only when the value is unmistakably the CMS crumb array. There is no third outcome and nothing is
 * ever dropped, re-ordered or re-shaped, so a route cannot lose structured data it ships now,
 * whatever the CMS starts sending.
 *
 * That constraint is the lesson of five failed versions of this function. Each tried to decide what
 * every element WAS — on the absence of `@type`, the absence of `@context`, the presence of
 * `href`/`name`, then a whole-array form of the same — and each misfiled a legitimate document,
 * twice publishing a breadcrumb hierarchy that did not exist and twice leaving a page with no
 * JSON-LD at all. The fault was never the predicate; it was that a wrong answer could delete
 * something. Now a wrong answer can only fail to add.
 *
 * Recognition is deliberately narrow — the shape seen on every one of ~50 sampled live listing
 * routes (a list of {name, href} pairs is not uniquely a breadcrumb, so this is the narrowest
 * recognition that covers production, not a proof of identity): at least two plain objects, own keys a subset of {href, name}, every `name` a
 * non-empty string, every `href` an absolute http(s) URL except on the last, where Google does not
 * require `item`. Any key that is not `href` or `name` — `@type` and `@context` included — means it is
 * somebody's document, and we add nothing rather than guess.
 */
const isCmsCrumbArray = (value: unknown): value is SeoCrumb[] => {
  if (!Array.isArray(value) || value.length < 2) return false;
  return value.every((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const keys = Object.keys(item);
    if (keys.length === 0 || !keys.every(key => key === 'href' || key === 'name')) return false;
    const crumb = item as SeoCrumb;
    if (!crumbName(crumb.name)) return false;
    return absoluteHttpHref(crumb.href) !== null || index === value.length - 1;
  });
};

export const listingJsonLds = (jsonld: unknown): object[] => {
  const asToday = jsonld == null ? [] : [jsonld as object];
  const breadcrumb = isCmsCrumbArray(jsonld) ? buildListingBreadcrumbJsonLd(jsonld) : null;
  return breadcrumb ? [breadcrumb, ...asToday] : asToday;
};

/**
 * ItemList of the first ten doctors of a listing — the entities themselves (Physician + the card's own
 * rating), not just links. Shared by the DR-41 experiment and the DR-43 area pages; honest values only:
 * a key the API did not send is dropped, and a doctor nobody rated gets no aggregateRating.
 */
export const buildDoctorItemList = ({ name: listName, total, cards }: { name: string; total: number; cards: SearchCard[] }) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  'name': listName,
  'numberOfItems': total,
  'itemListElement': cards
    .filter(card => card.type === 'doctor' && card.url)
    .slice(0, 10)
    .map((card, index) => {
      const url = `${ORIGIN}${encodeURI(decodeURI(card.url as string))}`;
      const name = [card.prefix, card.title].filter(Boolean).join(' ');
      return {
        '@type': 'ListItem',
        'position': index + 1,
        url,
        name,
        // The entity itself, not just a link: this is what lets an engine answer
        // «بهترین دکتر …» with our doctors and their ratings instead of a competitor's.
        'item': compact({
          '@type': 'Physician',
          name,
          url,
          image: card.image,
          aggregateRating: cardRating(card),
        }),
      };
    }),
});

export const buildListingSeoExperiment = ({
  asPath,
  routes,
  crumbs,
  total,
  result,
}: {
  asPath: string;
  routes: Array<string | null>;
  crumbs: SeoCrumb[] | undefined;
  total: number | undefined;
  result: SearchCard[] | undefined;
}): ListingSeoExperiment | null => {
  if (asPath.includes('?')) return null; // filtered / paginated / text views keep the CMS template
  const path = listingPath(asPath);
  if (!routes.some(route => route && listingPath(route) === path)) return null;

  const city = crumbs?.[1]?.name?.trim();
  const group = crumbs?.[crumbs.length - 1]?.name?.trim();
  if (!city || !group || !crumbs || crumbs.length < 3 || !total) return null;

  const cards = result ?? [];
  const slot = earliestSlot(cards);
  const soonDay = slot?.match(/^(امروز|فردا)/)?.[1];
  const reviews = cards.reduce((sum, card) => sum + (card.rates_count ?? 0), 0);

  const title = `بهترین دکتر ${group} ${city}، نوبت اینترنتی${soonDay ? ` از ${soonDay}` : ''}`;
  const description = [
    `${faNumber(total)} پزشک ${group} در ${city}`,
    slot ? `اولین نوبت خالی: ${slot}` : null,
    reviews > 0 ? `بیش از ${faNumber(reviews)} نظر بیماران` : null,
    'مقایسه و نوبت‌دهی آنلاین در پذیرش۲۴',
  ]
    .filter(Boolean)
    .join(' · ');

  const breadcrumbList = buildListingBreadcrumbJsonLd(crumbs);
  const itemList = buildDoctorItemList({ name: `بهترین دکتر ${group} ${city}`, total, cards });

  return { title, description, jsonlds: [breadcrumbList, itemList].filter(Boolean) as object[] };
};

export default buildListingSeoExperiment;
